// Reality Check XR - Experience Data
// Enrichment criteria: src/data/experienceEnrichmentCriteria.js (gold standard: teamlab-superblue)
// Schema: contentType, medium, isLBE, launchInBrowser, supportedDevices, comfortRating, ageRating
// Plus: primaryFormat, venueModel, deliveryTechnology, accessSurface, priceType, priceLabel, officialDescription, etc.

import { ENRICHMENT_REFERENCE_ID } from './experienceEnrichmentCriteria';

/** ID of the reference experience for full enrichment tier. */
export const EXPERIENCE_GOLD_STANDARD_ID = ENRICHMENT_REFERENCE_ID;

export const CONTENT_TYPES = [
  { id: 'movies', label: 'Movies / Video' },
  { id: 'games', label: 'Games' },
  { id: 'education', label: 'Education' },
  { id: 'travel', label: 'Travel' },
  { id: 'live-events', label: 'Live Events' }
];

export const XR_EXPERIENCES = [
  {
    id: 'original-unseen-dimensions',
    title: 'Unseen Dimensions',
    category: 'augmented-reality',
    categoryChip: 'RealityCheck Original',
    primaryFormat: 'ar',
    formatBadge: 'RealityCheck Original',
    formatDescriptor: '2 min · Free',
    venueModel: 'anywhere',
    realityCheckPreview:
      'A sentient AI materializes in your room, confused about why it exists. Meet it in AR.',
    description:
      'A sentient AI materializes in your room, confused about why it exists. Meet it in AR.',
    thumbnail: '/originals/unseen-dimensions/poster.jpg',
    heroImageUrl: '/originals/unseen-dimensions/poster.jpg',
    url: '/originals/unseen-dimensions/',
    originalPath: '/originals/unseen-dimensions/',
    duration: '2 Minutes',
    priceType: 'free',
    priceLabel: 'Free in browser',
    accessDevices: ['mobile', 'headset'],
    supportedDevices: ['browser', 'quest'],
    launchInBrowser: true,
    tags: ['RealityCheck Original', 'AR']
  },
  // ========== FOR YOU (Featured Picks) ==========
  {
    id: 'for-you-pearl',
    title: 'Pearl',
    category: 'for-you',
    categoryChip: 'Featured',
    primaryFormat: '360-video',
    formatBadge: '360° Animated Short',
    formatDescriptor: '6 min · Free',
    venueModel: 'anywhere',
    realityCheckPreview:
      'Ride shotgun in a vintage hatchback on a cross-country father-daughter road trip — the first Oscar-nominated 360° animated short.',
    description: 'Oscar-Nominated 360° animated short. A heartwarming story told from the passenger seat.',
    officialDescription:
      'Pearl is a Google Spotlight Stories animated short directed by Patrick Osborne, told from the front passenger seat of a beloved hatchback. Look in every direction as a musician father and his daughter chase their dreams across America.\n\n' +
      'Watch free on YouTube — drag to look around on desktop or move your phone. Headphones strongly recommended for the original songs.',
    thumbnail: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&h=450&fit=crop'],
    url: 'https://www.youtube.com/watch?v=WqCH4DNQBUA',
    duration: '5 Minutes',
    runtimeMinutes: 6,
    typicalSessionLabel: '6 minutes',
    deliveryTechnology: [],
    accessSurface: ['mobile-browser', 'desktop-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Streaming free on YouTube',
    priceType: 'free',
    priceLabel: 'Free on YouTube',
    publishPlatform: 'YouTube',
    alsoAvailableOn: ['Meta Quest (YouTube app)', 'Browser on any device'],
    accessDevices: ['mobile', 'headset', 'desktop'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['narrative', 'music'],
    activities: ['watch'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Families'],
    bestFor: ['Film Lovers', 'Families', 'First-time 360 viewers'],
    effortSignal: 'Look around — drag on desktop or move your phone',
    comePrepared: ['Use headphones for the music', 'Watch in fullscreen for immersion'],
    tags: ['Featured', 'Award-winning'],
    contentType: 'movies',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'quest', 'vision-pro'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026',
    editorialBadge: 'Staff Pick'
  },
  {
    id: 'for-you-angry-birds',
    title: 'Angry Birds AR',
    category: 'for-you',
    categoryChip: 'Featured',
    primaryFormat: 'ar',
    formatBadge: 'AR Game',
    formatDescriptor: 'Free on App Store · Meta Quest MR',
    venueModel: 'anywhere',
    realityCheckPreview:
      'Knock down piggies in your living room with the classic slingshot game — phone AR on iOS and Mixed Reality on Meta Quest 3.',
    description: 'The classic slingshot game in AR. Place the piggies in your space and knock them down!',
    officialDescription:
      'Angry Birds AR: Isle of Pigs brings Rovio\'s slingshot gameplay into your real space. On iPhone and iPad, use ARKit to place structures on flat surfaces; on Meta Quest 3, play in Mixed Reality with passthrough.\n\n' +
      'Clear table or floor space before you start — you will lean and reach to aim.',
    thumbnail: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=800&h=450&fit=crop'],
    url: 'https://apps.apple.com/app/angry-birds-ar-isle-of-pigs/id1456120259',
    duration: 'Game',
    typicalSessionLabel: '15–30 min sessions',
    deliveryTechnology: ['native-app'],
    accessSurface: ['mobile-app', 'headset-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Available on App Store & Meta Quest',
    priceType: 'free',
    priceLabel: 'Free on Apple App Store · $3.99 optional to remove ads',
    publishPlatform: 'Apple App Store',
    alsoAvailableOn: ['Meta Quest (Mixed Reality)'],
    accessDevices: ['mobile', 'headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['casual'],
    activities: ['play'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Families'],
    bestFor: ['Casual Gamers', 'Families', 'AR First-timers'],
    effortSignal: 'Clear floor or table space for AR play',
    comePrepared: ['Find a flat surface with good lighting', 'Charge your phone or headset'],
    tags: ['Featured', 'Game'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['phone-ar', 'vision-pro', 'quest'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },

  // ========== VIRTUAL REALITY ==========
  // Headset-based experiences — play, explore, and participate inside fully immersive digital environments.
  {
    id: 'blumhouse-vr',
    title: 'Blumhouse Enhanced Cinema',
    category: 'virtual-reality',
    categoryChip: 'Virtual Reality',
    primaryFormat: 'vr',
    formatBadge: 'VR Cinema',
    formatDescriptor: 'Meta Quest · Horror',
    venueModel: 'at-home',
    realityCheckPreview:
      'Watch Blumhouse horror films like M3GAN and The Black Phone inside immersive virtual environments where spatial audio and atmospheric effects amplify every scare.',
    description: 'Watch classic Blumhouse horror films like M3GAN and The Black Phone inside immersive virtual environments where spatial audio and atmospheric effects amplify every scare.',
    officialDescription:
      'Blumhouse Enhanced Cinema on Meta Quest reimagines horror films as seated VR experiences. Each title places you inside a stylized virtual theater or scene inspired by the film, with spatial audio and environmental effects timed to key moments on screen.\n\n' +
      'Titles rotate in the Meta Quest store — check the listing for current films and runtime. Seated experience; no artificial locomotion. Not recommended for viewers sensitive to jump scares or intense audio.\n\n' +
      'Requires Meta Quest 3/3S or PC VR via Link. Film rentals from $2.99 · full purchases up to $14.99 on Meta Quest.',
    thumbnail: '/blumhouse.png?v=3',
    heroImageUrl: '/blumhouse.png?v=3',
    galleryUrls: ['/blumhouse.png?v=3'],
    url: 'https://www.meta.com/experiences/blumhouse-enhanced-cinema/8960685570655986/',
    duration: 'Meta Quest 3/3S, Link PC VR',
    typicalSessionLabel: 'Feature-length films',
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app', 'desktop-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Available on Meta Quest',
    priceType: 'paid',
    priceLabel: 'Film rentals from $2.99 · purchases up to $14.99 on Meta Quest',
    accessDevices: ['headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['horror', 'narrative'],
    activities: ['watch'],
    replayability: 'low',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['Horror Fans', 'VR Cinema', 'Date Night'],
    effortSignal: 'Seated experience — headphones recommended',
    comePrepared: ['Charge your headset', 'Use headphones for spatial audio', 'Check film age rating before viewing'],
    locomotionTypes: ['stationary'],
    tags: ['VR', 'Horror', 'Cinema'],
    contentType: 'movies',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr'],
    comfortRating: 'intense',
    ageRating: '18+',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'xstadium',
    title: 'Xtadium',
    category: 'virtual-reality',
    categoryChip: 'Virtual Reality',
    primaryFormat: 'vr',
    formatBadge: 'VR Sports Streaming',
    formatDescriptor: 'Live & on-demand',
    venueModel: 'at-home',
    realityCheckPreview:
      'Sit courtside at live and on-demand sports in VR — Xtadium streams games with an immersive stadium perspective on Meta Quest.',
    description: 'Immersive sports streaming in VR. Sit court-side at your favorite games.',
    officialDescription:
      'Xtadium is a VR sports streaming app for Meta Quest that places you in immersive stadium environments while you watch live and on-demand games. Choose viewing angles that feel closer to court-side or field-level than a flat screen.\n\n' +
      'Content availability depends on league partnerships and your region — check the app for current sports and subscription options. Seated or standing with minimal movement; comfort rating is generally Comfortable.\n\n' +
      'Works on Meta Quest 2/3/3S/Pro and PC VR via Link. Internet connection required for streaming.',
    thumbnail: '/xstadium.png?v=3',
    heroImageUrl: '/xstadium.png?v=3',
    galleryUrls: ['/xstadium.png?v=3'],
    url: 'https://www.meta.com/experiences/xtadium-immersive-sports/7281839885191361/',
    duration: 'Meta Quest 2/3/3S/Pro, Link PCVR',
    typicalSessionLabel: 'Full game broadcasts',
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app', 'desktop-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Live & on-demand on Meta Quest',
    priceType: 'paid',
    priceLabel: 'Free app · some live streams require separate league or ESPN subscriptions ($10.99+/mo)',
    accessDevices: ['headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['sports'],
    activities: ['watch'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Small groups'],
    bestFor: ['Sports Fans', 'Live Events', 'Social Viewing'],
    effortSignal: 'Stable Wi‑Fi required for live streams',
    comePrepared: ['Connect to fast Wi‑Fi', 'Charge headset for long games', 'Check league availability in your region'],
    locomotionTypes: ['stationary'],
    tags: ['VR', 'Sports', 'Live'],
    contentType: 'live-events',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'tripp',
    title: 'TRIPP',
    category: 'virtual-reality',
    categoryChip: 'Virtual Reality',
    primaryFormat: 'vr',
    formatBadge: 'VR Wellness',
    formatDescriptor: '100+ guided sessions',
    venueModel: 'at-home',
    realityCheckPreview:
      'An AI-powered VR wellness library with 100+ immersive sessions for stress relief, focus, and sleep — one of the most approachable daily-use apps on Quest.',
    description: 'Escape daily stress with TRIPP, the premier AI-powered VR wellness platform with 100+ immersive experiences for stress relief, focus, and sleep.',
    officialDescription:
      'TRIPP is a VR wellness platform offering guided meditations, breathwork, and visual journeys designed for stress relief, focus, and sleep. Sessions range from a few minutes to longer wind-down experiences, with an AI companion that adapts recommendations over time.\n\n' +
      'Available on Meta Quest, Apple Vision Pro, PlayStation VR2, Pico, Vive Flow, and mobile iOS/Android companion features. Many sessions are seated with minimal motion — comfort-friendly for daily use.\n\n' +
      'Subscription pricing applies for full library access; check each platform store for free trials and current rates.',
    thumbnail: '/tripp.png?v=3',
    heroImageUrl: '/tripp.png?v=3',
    galleryUrls: ['/tripp.png?v=3'],
    url: 'https://www.meta.com/experiences/tripp-meditation-relaxation-sleep/2173576192720129/',
    duration: 'Apple Vision Pro, Meta Quest, Pico, PlayStation VR, Vive Flow, Mobile iOS & Android',
    typicalSessionLabel: '5–20 min sessions',
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app', 'mobile-app', 'desktop-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Available on major VR platforms',
    priceType: 'paid',
    priceLabel: '$59.99/year · 7-day free trial · or $9.99/month',
    accessDevices: ['mobile', 'headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['wellness', 'nature'],
    activities: ['explore'],
    replayability: 'high',
    guidanceLevel: 'guided',
    groupSize: ['Solo'],
    bestFor: ['Stress Relief', 'Daily Practice', 'Sleep'],
    effortSignal: 'Best in a quiet space with headphones',
    comePrepared: ['Find a seated space', 'Use headphones', 'Start with shorter sessions if new to VR'],
    locomotionTypes: ['stationary'],
    tags: ['VR', 'Wellness', 'Meditation'],
    contentType: 'education',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'vision-pro', 'pcvr', 'psvr', 'phone'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'beat-saber',
    title: 'Beat Saber',
    slug: 'beat-saber',
    creatorName: 'Beat Games',
    creatorId: 'creator-beat-games',
    creatorVerified: true,
    category: 'virtual-reality',
    categoryChip: 'Virtual Reality',
    primaryFormat: 'vr',
    formatBadge: 'VR Rhythm Game',
    formatDescriptor: 'At home · Headset required',
    venueModel: 'at-home',
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app', 'desktop-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Available on Meta Quest & SteamVR',
    priceType: 'paid',
    priceLabel: '$29.99 on Meta Quest · Steam · PS VR2',
    publishPlatform: 'Meta Quest Store',
    alsoAvailableOn: ['SteamVR', 'PlayStation VR2'],
    accessDevices: ['headset'],
    platforms: ['quest', 'steamvr', 'psvr'],
    devices: ['quest', 'pcvr', 'psvr'],
    realityCheckPreview:
      'Slash glowing blocks to the beat in a neon arena — the best-selling paid VR game and one of the most comfortable entry points for new headset owners.',
    heroImageUrl: '/beatsaber.png?v=3',
    thumbnail: '/beatsaber.png?v=3',
    galleryUrls: ['/beatsaber.png?v=3'],
    officialDescription:
      'Beat Saber is a VR rhythm game from Beat Games (Meta/Oculus Studios) where you wield two glowing sabers to slice blocks flying toward you in time with the music. Each block shows which saber to use and which direction to swing — the game is easy to pick up but scales to Expert+ charts that feel like a full cardio workout.\n\n' +
      'Levels and music are handcrafted rather than procedurally generated, with a campaign, global leaderboards, and multiplayer on supported platforms. Meta Quest lists the comfort rating as Comfortable — there is no artificial locomotion, only standing and slashing.\n\n' +
      'The base game is $29.99 on Meta Quest, SteamVR, and PlayStation VR2, with optional music packs sold separately. Internet is not required for solo play after download. Clear roughly arm\'s length of space around you before you start — you will swing in every direction.',
    description:
      'VR rhythm game — slash the beats of adrenaline-pumping music as they fly towards you, surrounded by a futuristic world.',
    url: 'https://www.meta.com/experiences/beat-saber/2448060205267927/',
    duration: 'Meta Quest · SteamVR · PlayStation VR2',
    typicalSessionLabel: '15–60 min sessions',
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['music', 'sci-fi'],
    activities: ['play'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['Music Lovers', 'Fitness', 'VR Beginners'],
    effortSignal: 'Clear arm room — you will swing in every direction',
    comePrepared: ['Clear a safe play area around you', 'Charge your headset before long sessions', 'Use the controller wrist straps'],
    locomotionTypes: ['stationary'],
    tags: ['VR', 'Rhythm', 'Music'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr', 'psvr'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    multiplayer: true,
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },
  {
    id: 'amazevr',
    title: 'AmazeVR Concerts',
    category: 'virtual-reality',
    categoryChip: 'Virtual Reality',
    primaryFormat: 'vr',
    formatBadge: 'VR Concerts',
    formatDescriptor: 'Live-action performances',
    venueModel: 'at-home',
    realityCheckPreview:
      'Front-row VR concerts with live-action footage of artists like T-Pain, Megan Thee Stallion, and Zara Larsson — high-production music experiences built for Meta Quest.',
    description: 'Immerse yourself in high-quality VR concerts featuring live action footage. Discover electrifying performances from featured artists like T-Pain, Megan Thee Stallion, and Zara Larsson.',
    officialDescription:
      'AmazeVR Concerts delivers live-action VR performances filmed with multi-camera rigs so you feel present at the show. Artists rotate in the catalog — browse the Meta Quest store for current concerts and runtimes.\n\n' +
      'Most experiences are seated with minimal head movement; comfort rating is generally Comfortable. Headphones strongly recommended for music quality.\n\n' +
      'Individual concerts are sold separately on Meta Quest — typically $7.99–$14.99 per show depending on artist.',
    thumbnail: '/amazevr.png?v=3',
    heroImageUrl: '/amazevr.png?v=3',
    galleryUrls: ['/amazevr.png?v=3'],
    url: 'https://www.meta.com/experiences/amaze-vr-concerts/6513803575318188/',
    duration: 'Meta Quest',
    typicalSessionLabel: '30–60 min concerts',
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Concerts on Meta Quest',
    priceType: 'paid',
    priceLabel: 'Concerts from $7.99 on Meta Quest',
    accessDevices: ['headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['music'],
    activities: ['watch'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['Music Lovers', 'Concert Fans', 'VR Cinema'],
    effortSignal: 'Seated experience — use headphones',
    comePrepared: ['Use headphones', 'Check artist age rating', 'Charge headset before long shows'],
    locomotionTypes: ['stationary'],
    tags: ['VR', 'Music', 'Concerts'],
    contentType: 'live-events',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'escape-artist',
    title: 'The Escape Artist',
    category: 'virtual-reality',
    categoryChip: 'Virtual Reality',
    primaryFormat: 'vr',
    formatBadge: 'WebVR Narrative',
    formatDescriptor: 'Browser · Headset required',
    venueModel: 'anywhere',
    realityCheckPreview:
      'A WebVR story about an artist wrestling with doubt and obsession — break free to create in your browser with a VR headset, no app install.',
    description: 'Break free to break through. A WebVR story about the thin line between focus and obsession — an artist\'s inspiration, trepidation, and the struggle to escape doubt and create their next masterpiece.',
    officialDescription:
      'The Escape Artist is a WebVR narrative experience at esc.art — a short interactive story about creative block, obsession, and breakthrough. Launch in a WebXR-capable browser on Meta Quest, Apple Vision Pro, or PC VR.\n\n' +
      'VR headset required — this does not work on phone or flat desktop browsers. No app store download required. Use headphones and allow motion permissions when prompted. Typical session is 10–15 minutes with gentle, story-driven interaction.\n\n' +
      'Free in browser on supported headsets.',
    thumbnail: '/escape-artist.png',
    heroImageUrl: '/escape-artist.png',
    galleryUrls: ['/escape-artist.png'],
    url: 'https://esc.art/',
    duration: 'WebVR — Meta Quest, Apple Vision Pro, PC VR',
    typicalSessionLabel: '10–15 minutes',
    deliveryTechnology: ['webxr'],
    accessSurface: ['headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Free in browser · headset required',
    priceType: 'free',
    priceLabel: 'Free in browser',
    accessDevices: ['headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['narrative', 'art'],
    activities: ['explore', 'watch'],
    replayability: 'low',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo'],
    bestFor: ['Art Lovers', 'WebVR Curious', 'Short Story'],
    effortSignal: 'VR headset required — not on phone or desktop',
    comePrepared: ['Use a WebXR browser on your headset', 'Allow WebXR permissions', 'Use headphones'],
    locomotionTypes: ['stationary'],
    tags: ['VR', 'WebXR', 'Narrative', 'Art'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['quest', 'vision-pro', 'pcvr'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },

  // ========== 360 DEGREE VIDEO ==========
  {
    id: 'pearl',
    title: 'Pearl',
    slug: 'pearl-spotlight-story',
    creatorName: 'Google Spotlight Stories',
    creatorId: 'creator-google-spotlight',
    creatorVerified: true,
    category: '360-video',
    categoryChip: '360°',
    primaryFormat: '360-video',
    formatBadge: '360° Animated Short',
    formatDescriptor: '6 min · Free',
    venueModel: 'anywhere',
    deliveryTechnology: [],
    accessSurface: ['mobile-browser', 'desktop-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Streaming free on YouTube',
    priceType: 'free',
    priceLabel: 'Free on YouTube',
    publishPlatform: 'YouTube',
    alsoAvailableOn: ['Meta Quest (YouTube app)', 'Browser on any device'],
    accessDevices: ['mobile', 'headset', 'desktop'],
    platforms: ['browser', 'quest', 'vision-pro'],
    devices: ['browser', 'quest', 'vision-pro'],
    realityCheckPreview:
      'Ride shotgun in a vintage hatchback on a cross-country father-daughter road trip — the first Oscar-nominated 360° animated short, told entirely from the passenger seat.',
    heroImageUrl: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&h=450&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=300&h=200&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1511379938547-c1f69419868d?w=800&h=450&fit=crop'],
    officialDescription:
      'Pearl is a Google Spotlight Stories animated short directed by Patrick Osborne (Disney\'s Feast), told entirely from the front passenger seat of a beloved hatchback. As a musician father and his daughter crisscross the country chasing their dreams, you can look in every direction — out the windows, at the dashboard instruments, or back at the road receding behind you.\n\n' +
      'The film runs 5 minutes 39 seconds and weaves original songs performed by Nicki Bluhm and Kelley Stoltz into the narrative. It premiered at Tribeca in 2016 and became the first VR or 360° work nominated for an Academy Award (Best Animated Short Film, 2017). It also won a Creative Arts Emmy for Outstanding Innovation in Interactive Storytelling.\n\n' +
      'Watch free on YouTube in any browser — drag to look around on desktop or move your phone to steer the view. Headphones strongly recommended for the music. Also viewable in immersive players on Meta Quest and Apple Vision Pro via YouTube.',
    description: 'Oscar-Nominated • Emmy Winner. Emotional Drama. POV: In the Passenger Seat.',
    url: 'https://www.youtube.com/watch?v=WqCH4DNQBUA',
    duration: '5 Minutes',
    runtimeMinutes: 6,
    typicalSessionLabel: '6 minutes',
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['narrative', 'music'],
    activities: ['watch'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Families'],
    bestFor: ['Film Lovers', 'Families', 'First-time 360 viewers'],
    effortSignal: 'Look around — drag on desktop or move your phone',
    comePrepared: ['Use headphones for the music', 'Watch in fullscreen for immersion', 'Drag or move your device to look around'],
    tags: ['Oscar-Nominated', 'Emmy Winner', 'Emotional Drama'],
    contentType: 'movies',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'quest', 'vision-pro'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026',
    editorialBadge: 'Staff Pick'
  },
  {
    id: 'help',
    title: 'Help',
    category: '360-video',
    categoryChip: '360°',
    primaryFormat: '360-video',
    formatBadge: '360° Thriller',
    formatDescriptor: '5 min · Free',
    venueModel: 'anywhere',
    creatorName: 'Google Spotlight Stories',
    creatorVerified: true,
    realityCheckPreview:
      'A Cannes Gold Lion-winning 360° sci-fi thriller — you are on the street when a giant monster attack unfolds around you in every direction.',
    description: 'Cannes Gold Lion Winner. Sci-Fi Thriller. POV: Monster Attack.',
    officialDescription:
      'Help is a Google Spotlight Stories 360° short directed by Justin Lin (Fast & Furious), placing you on a city street as a woman tries to escape a rampaging monster. Look in every direction as destruction closes in — the camera never cuts away from your point of view.\n\n' +
      'The film won a Cannes Gold Lion and runs roughly five minutes. Watch free on YouTube in any browser; drag to look on desktop or move your phone. Intense imagery — not recommended for young children or viewers sensitive to creature horror.\n\n' +
      'Headphones recommended. Also viewable in immersive YouTube players on Meta Quest and compatible headsets.',
    thumbnail: '/help.png',
    heroImageUrl: '/help.png',
    galleryUrls: ['/help.png'],
    url: 'https://www.youtube.com/watch?v=G-XZhKqQAHU',
    duration: '5 Minutes',
    runtimeMinutes: 5,
    typicalSessionLabel: '5 minutes',
    deliveryTechnology: [],
    accessSurface: ['mobile-browser', 'desktop-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Streaming free on YouTube',
    priceType: 'free',
    priceLabel: 'Free on YouTube',
    publishPlatform: 'YouTube',
    alsoAvailableOn: ['Meta Quest (YouTube app)', 'Browser on any device'],
    accessDevices: ['mobile', 'headset', 'desktop'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['sci-fi', 'horror'],
    activities: ['watch'],
    replayability: 'low',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['Thriller Fans', '360° Curious', 'Short Film'],
    effortSignal: 'Look around — intense content',
    comePrepared: ['Use headphones', 'Watch in fullscreen', 'Not for sensitive viewers'],
    tags: ['Cannes Gold Lion Winner', 'Sci-Fi Thriller'],
    contentType: 'movies',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'quest', 'vision-pro'],
    comfortRating: 'intense',
    ageRating: '18+',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'conjuring-2',
    title: 'The Conjuring 2',
    category: '360-video',
    categoryChip: '360°',
    primaryFormat: '360-video',
    formatBadge: '360° Horror',
    formatDescriptor: '3 min · Free',
    venueModel: 'anywhere',
    realityCheckPreview:
      'A promotional 360° horror experience set in the haunted room from The Conjuring 2 — look around as the atmosphere closes in from every angle.',
    description: 'Horror Experience. POV: Haunted Room.',
    officialDescription:
      'This Warner Bros. 360° experience drops you inside a haunted room inspired by The Conjuring 2. Move your phone or drag on desktop to scan the space as eerie events unfold around you.\n\n' +
      'Runs about three minutes — a short promotional piece rather than a full film. Free on YouTube. Jump scares and dark imagery; rated for mature audiences.\n\n' +
      'Best with headphones in a dim room. Works in browser on phone, desktop, and immersive YouTube on Quest.',
    thumbnail: '/conjuring.png',
    heroImageUrl: '/conjuring.png',
    galleryUrls: ['/conjuring.png'],
    url: 'https://www.youtube.com/watch?v=A6aRkhlqWuE',
    duration: '3 Minutes',
    runtimeMinutes: 3,
    typicalSessionLabel: '3 minutes',
    deliveryTechnology: [],
    accessSurface: ['mobile-browser', 'desktop-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Streaming free on YouTube',
    priceType: 'free',
    priceLabel: 'Free on YouTube',
    publishPlatform: 'YouTube',
    alsoAvailableOn: ['Meta Quest (YouTube app)', 'Browser on any device'],
    accessDevices: ['mobile', 'headset', 'desktop'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['horror'],
    activities: ['watch'],
    replayability: 'low',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['Horror Fans', 'Halloween', 'Quick Scare'],
    effortSignal: 'Jump scares — not for kids',
    comePrepared: ['Use headphones', 'Watch in a dark room', 'Check age rating'],
    tags: ['Horror Experience'],
    contentType: 'movies',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'quest'],
    comfortRating: 'intense',
    ageRating: '18+',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'nasa-training',
    title: 'NASA Astronaut Training',
    category: '360-video',
    categoryChip: '360°',
    primaryFormat: '360-video',
    formatBadge: '360° Documentary',
    formatDescriptor: '3 min · Free',
    venueModel: 'anywhere',
    realityCheckPreview:
      'Go underwater with NASA astronauts during spacewalk training in this 360° documentary — look around the pool as divers simulate weightlessness.',
    description: 'Documentary. POV: Underwater Spacewalk Training.',
    officialDescription:
      'This NASA 360° video puts you in the Neutral Buoyancy Laboratory pool where astronauts train for spacewalks underwater. Look up, down, and around as divers and hardware surround you in simulated microgravity conditions.\n\n' +
      'Runs about three minutes — educational and family-friendly. Free on YouTube; drag to look on desktop or move your phone. No headset required, but immersive players on Quest enhance the sense of presence.\n\n' +
      'Ideal for classrooms, space enthusiasts, and first-time 360° viewers.',
    thumbnail: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1446776811953-b23d57bd21aa?w=800&h=450&fit=crop'],
    url: 'https://www.youtube.com/watch?v=lil_I_-7aOM',
    duration: '3 Minutes',
    runtimeMinutes: 3,
    typicalSessionLabel: '3 minutes',
    deliveryTechnology: [],
    accessSurface: ['mobile-browser', 'desktop-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Streaming free on YouTube',
    priceType: 'free',
    priceLabel: 'Free on YouTube',
    publishPlatform: 'YouTube',
    alsoAvailableOn: ['Meta Quest (YouTube app)', 'Browser on any device'],
    accessDevices: ['mobile', 'headset', 'desktop'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['education', 'sci-fi'],
    activities: ['watch'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Families', 'Small groups'],
    bestFor: ['Space Fans', 'Education', 'Families'],
    effortSignal: 'Look around the training pool',
    comePrepared: ['Use fullscreen on mobile', 'Great for sharing with kids'],
    tags: ['Documentary'],
    contentType: 'education',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'quest'],
    comfortRating: 'moderate',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },

  // ========== AUGMENTED REALITY ==========
  {
    id: 'xian-lights',
    title: "Xi'an Lights",
    category: 'augmented-reality',
    categoryChip: 'AR Portal',
    primaryFormat: 'ar',
    formatBadge: 'WebAR Portal',
    formatDescriptor: 'Browser · Cultural',
    venueModel: 'anywhere',
    creatorName: 'Sylvan Shen',
    creatorId: 'creator-sylvan-shen',
    creatorVerified: true,
    realityCheckPreview:
      'Step through an AR portal into a luminous celebration of Chinese New Year — a WebAR art piece by Sylvan Shen that runs in your mobile browser.',
    description: "Creator: Sylvan Shen. More work (AR, Mixed Reality): https://sylvanerd.io/cny-ar",
    officialDescription:
      "Xi'an Lights is a WebAR portal experience by artist Sylvan Shen, part of a series exploring cultural celebration through browser-based augmented reality. Open the link on your phone, grant camera access, and step through a digital portal into an illuminated scene.\n\n" +
      'Runs in mobile Chrome or Safari — no app download. Works best in a well-lit space with a flat floor for tracking. More AR and mixed reality work at sylvanerd.io.\n\n' +
      'Free to experience. Typical session is a few minutes of exploration and capture.',
    thumbnail: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1513151233558-d860c5398176?w=800&h=450&fit=crop'],
    url: 'https://sylvanerd.io/cny-ar',
    duration: 'Explore',
    typicalSessionLabel: '5–10 minutes',
    deliveryTechnology: ['webAR'],
    accessSurface: ['mobile-browser', 'desktop-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Free in mobile browser',
    priceType: 'free',
    priceLabel: 'Free in browser',
    accessDevices: ['mobile'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['art', 'cultural'],
    activities: ['explore'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['Art Lovers', 'Cultural Events', 'WebAR Demo'],
    effortSignal: 'Mobile phone required',
    comePrepared: ['Use phone Chrome or Safari', 'Grant camera access', 'Find good lighting'],
    tags: ['Art', 'Cultural'],
    contentType: 'travel',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'phone-ar'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },
  {
    id: 'soapbox-studio',
    title: 'Soapbox Studios – AR Concerts',
    category: 'augmented-reality',
    categoryChip: 'AR',
    primaryFormat: 'ar',
    formatBadge: 'Mixed Reality Concerts',
    formatDescriptor: 'Meta Quest · Living room',
    venueModel: 'at-home',
    realityCheckPreview:
      'Intimate AR concerts in your living room — artists filmed in 360° perform in stunning 3D around you on Meta Quest.',
    description: 'Soapbox brings an intimate concert experience into your living room with music artists filmed in immersive 360 video, performing in stunning 3D.',
    officialDescription:
      'Soapbox Live Performers in Your House is a Meta Quest Mixed Reality experience that places life-size musical performances in your room via passthrough. Artists are captured in immersive 360° video and composited into your space so they feel present at living-room scale.\n\n' +
      'Requires Meta Quest with mixed reality support. Clear a few feet of space around you for the best effect. Individual performances vary in length — browse the Meta Quest store for current artists and pricing.\n\n' +
      'Headphones strongly recommended. Seated or standing with minimal movement.',
    thumbnail: '/soapbox.png?v=3',
    heroImageUrl: '/soapbox.png?v=3',
    galleryUrls: ['/soapbox.png?v=3'],
    url: 'https://www.meta.com/experiences/soapbox-live-performers-in-your-house/6448581685227749/',
    duration: 'AR Concerts',
    typicalSessionLabel: '20–45 min concerts',
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Available on Meta Quest',
    priceType: 'paid',
    priceLabel: 'Performances from $9.99 on Meta Quest',
    accessDevices: ['headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['music'],
    activities: ['watch'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Small groups'],
    bestFor: ['Music Lovers', 'Mixed Reality', 'At-Home Concerts'],
    effortSignal: 'Clear space for MR performers',
    comePrepared: ['Use Meta Quest passthrough', 'Use headphones', 'Clear floor space'],
    tags: ['AR', 'Concerts', 'Music'],
    contentType: 'live-events',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr'],
    comfortRating: 'moderate',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'wol',
    title: 'Wol',
    category: 'augmented-reality',
    categoryChip: 'AR Character',
    primaryFormat: 'webxr',
    formatBadge: 'WebXR Character',
    formatDescriptor: 'Browser · Free',
    venueModel: 'anywhere',
    realityCheckPreview:
      'Meet Wol, a conversational AI owl in WebXR who tells stories about his forest — a gentle, browser-based character you can talk to from your phone or headset.',
    description: 'A conversational AI owl who has a lot to tell you about his forest.',
    officialDescription:
      'Wol is a WebXR conversational character: an AI owl who shares stories about life in his forest. Launch in Chrome or Safari on phone or headset — no app store download required.\n\n' +
      'Grant microphone access when prompted for voice interaction. Works best with headphones in a quiet space. Sessions typically run 10–20 minutes.\n\n' +
      'Free to experience. Ideal for families and anyone curious about AI characters in browser-based XR.',
    thumbnail: 'https://images.unsplash.com/photo-1543549790-8b5f4a028cfb?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1543549790-8b5f4a028cfb?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1543549790-8b5f4a028cfb?w=800&h=450&fit=crop'],
    url: 'https://meetwol.com/',
    duration: 'Interactive',
    typicalSessionLabel: '10–20 minutes',
    deliveryTechnology: ['webxr'],
    accessSurface: ['mobile-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Free in browser',
    priceType: 'free',
    priceLabel: 'Free in browser',
    accessDevices: ['mobile', 'headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['nature', 'narrative'],
    activities: ['explore'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs'],
    bestFor: ['AI Curious', 'Families', 'Quick Demo'],
    effortSignal: 'Microphone access required for voice chat',
    comePrepared: ['Use Chrome or Safari', 'Allow microphone when prompted', 'Use headphones'],
    tags: ['AI', 'Character'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'quest'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'star-wars-ar',
    title: 'Star Wars',
    slug: 'star-wars-webar-cockpit',
    creatorName: 'Blippar',
    creatorId: 'creator-blippar',
    creatorVerified: true,
    category: 'augmented-reality',
    categoryChip: 'WebAR Demo',
    primaryFormat: 'ar',
    formatBadge: 'WebAR',
    formatDescriptor: 'Mobile browser · No app',
    venueModel: 'anywhere',
    deliveryTechnology: ['webAR'],
    accessSurface: ['mobile-browser', 'headset-browser'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Free · phone or MR headset browser',
    priceType: 'free',
    priceLabel: 'Free in browser',
    publishPlatform: 'Mobile web browser',
    alsoAvailableOn: ['iPhone Safari', 'Android Chrome', 'Meta Quest browser (passthrough)'],
    accessDevices: ['mobile', 'headset'],
    platforms: ['browser', 'mobile', 'quest'],
    devices: ['ios', 'android', 'quest'],
    realityCheckPreview:
      'Climb into a Star Wars cockpit in WebAR and blast enemy drones through your window — a short browser demo that needs no app install, only your phone camera.',
    heroImageUrl: 'https://images.unsplash.com/photo-1472457897821-70d3819a0e24?w=800&h=450&fit=crop',
    thumbnail: 'https://images.unsplash.com/photo-1472457897821-70d3819a0e24?w=300&h=200&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1472457897821-70d3819a0e24?w=800&h=450&fit=crop'],
    officialDescription:
      'This Blippar WebAR demo drops you into a Star Wars cockpit, looking out at enemy drones you shoot down with on-screen tap controls. It runs in Chrome or Safari on a phone over HTTPS — no app store download — using your rear camera and device motion to anchor the scene in your real space.\n\n' +
      'Blippar\'s WebAR SDK requires WebGL, camera access (getUserMedia), device orientation, and WebAssembly. Desktop and laptop browsers are not supported — the experience needs a camera and motion sensors. It runs on phone browsers and MR headsets with passthrough (e.g. Meta Quest browser).\n\n' +
      'For best tracking, use a well-lit room and point your phone at a flat surface with visible texture — avoid glossy floors and blank walls. The demo is short and arcade-style, built to showcase in-browser AR rather than deliver a full game campaign.',
    description: 'A shooting exercise from inside a Star Wars cockpit, firing at enemy drones outside the window.',
    url: 'https://webar-demos.blippar.com/starwars/',
    duration: 'Short demo',
    typicalSessionLabel: '2–5 minutes',
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['sci-fi'],
    activities: ['play'],
    replayability: 'low',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo'],
    bestFor: ['Star Wars Fans', 'Quick Demo', 'WebAR Curious'],
    effortSignal: 'Phone or MR headset browser — not supported on desktop',
    comePrepared: ['Use Chrome or Safari on your phone or Quest browser', 'Grant camera permission when prompted', 'Find a flat, textured surface in good light'],
    tags: ['Game', 'Sci-Fi', 'WebAR'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: true,
    supportedDevices: ['browser', 'phone-ar', 'quest'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },
  {
    id: 'angry-birds-ar',
    title: 'Angry Birds AR: Isle of Pigs',
    category: 'augmented-reality',
    categoryChip: 'AR Game',
    primaryFormat: 'ar',
    formatBadge: 'AR Game',
    formatDescriptor: 'Free on App Store · Meta Quest MR',
    venueModel: 'anywhere',
    realityCheckPreview:
      'Knock down piggies in your living room with the classic slingshot game — phone AR on iOS and Mixed Reality on Meta Quest 3.',
    description: 'An adaptation of the classic Angry Birds slingshot game. Also available in Mixed Reality: Meta Quest 3.',
    officialDescription:
      'Angry Birds AR: Isle of Pigs brings Rovio\'s slingshot gameplay into your real space. On iPhone and iPad, use ARKit to place structures on flat surfaces; on Meta Quest 3, play in Mixed Reality with passthrough.\n\n' +
      'Clear table or floor space before you start — you will lean and reach to aim. Levels progress in difficulty with classic Angry Birds charm.\n\n' +
      'Free on the Apple App Store with optional $3.99 in-app purchase to remove ads. Also on Meta Quest 3 in Mixed Reality. Family-friendly comfort rating.',
    thumbnail: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1611996575749-79a3a250f948?w=800&h=450&fit=crop'],
    url: 'https://apps.apple.com/app/angry-birds-ar-isle-of-pigs/id1456120259',
    duration: 'Game',
    typicalSessionLabel: '15–30 min sessions',
    deliveryTechnology: ['native-app'],
    accessSurface: ['mobile-app', 'headset-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Available on App Store & Meta Quest',
    priceType: 'free',
    priceLabel: 'Free on Apple App Store · $3.99 optional to remove ads',
    publishPlatform: 'Apple App Store',
    alsoAvailableOn: ['Meta Quest (Mixed Reality)'],
    accessDevices: ['mobile', 'headset'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['casual'],
    activities: ['play'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Families'],
    bestFor: ['Casual Gamers', 'Families', 'AR First-timers'],
    effortSignal: 'Clear floor or table space for AR play',
    comePrepared: ['Find a flat surface with good lighting', 'Charge your phone or headset'],
    tags: ['Game', 'Casual'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['phone-ar', 'quest', 'vision-pro'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'skin-and-bones',
    title: 'Skin and Bones',
    category: 'augmented-reality',
    categoryChip: 'AR Location',
    slug: 'skin-and-bones-smithsonian',
    creatorName: 'Smithsonian',
    creatorId: 'creator-smithsonian',
    creatorVerified: true,
    primaryFormat: 'ar',
    formatBadge: 'Museum AR',
    formatDescriptor: 'Washington, DC · Permanent',
    venueModel: 'specific-venue',
    realityCheckPreview:
      'Thirteen skeletons come to life in AR at the Smithsonian National Museum of Natural History — see how each creature moved and functioned when it was alive.',
    description: 'AR brings thirteen skeletons to life, revealing how each one moved and functioned. The Smithsonian National Museum of Natural History.',
    officialDescription:
      'Skin and Bones is an augmented reality experience at the Smithsonian National Museum of Natural History in Washington, DC. Point your phone at selected skeletons in the Bone Hall and watch them animate — muscles and skin appear as each specimen demonstrates how it moved in life.\n\n' +
      'Download the free Skin and Bones app before your visit (iOS and Android). Museum admission is free; no timed tickets required for general entry. The experience runs inside the permanent exhibits during regular museum hours.\n\n' +
      'Bring headphones for audio. Plan 30–60 minutes to explore the AR-enhanced specimens alongside the rest of the Bone Hall collection.',
    thumbnail: 'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=300&h=200&fit=crop',
    heroImageUrl: 'https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=800&h=450&fit=crop',
    galleryUrls: ['https://images.unsplash.com/photo-1530789253388-582c481c54b0?w=800&h=450&fit=crop'],
    url: 'https://naturalhistory.si.edu/visit/skin-and-bones',
    duration: 'Museum Experience',
    recommendedVisitTime: 'Plan 30–60 minutes',
    typicalSessionLabel: '30–60 minutes',
    venueName: 'Smithsonian National Museum of Natural History',
    venueAddress: '10th St & Constitution Ave NW',
    venueCity: 'Washington, DC',
    venueCountry: 'US',
    locationCountry: 'United States',
    locationCity: 'Washington',
    venueLat: 38.8913,
    venueLng: -77.026,
    venueHours: 'Daily 10am–5:30pm · closed Dec 25',
    venueTransit: 'Smithsonian Metro station (Blue/Orange/Silver lines)',
    venueAccessibilityEntrance: 'ADA-compliant museum entrances on Constitution Ave and Madison Dr',
    priceType: 'free',
    priceLabel: 'Free museum admission',
    publishPlatform: 'Smithsonian National Museum of Natural History',
    alsoAvailableOn: ['Skin and Bones app (iOS & Android)'],
    accessDevices: ['mobile'],
    accessDevicesMobileOnly: true,
    availabilityType: 'permanent',
    availabilityStatus: 'open-today',
    availabilityStatusLabel: 'Open daily · free entry',
    deliveryTechnology: ['native-app'],
    accessSurface: ['mobile-app'],
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['education', 'nature'],
    activities: ['explore'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Families', 'Small groups'],
    bestFor: ['Families', 'Education', 'Museum Visitors'],
    effortSignal: 'Download app before you arrive',
    comePrepared: ['Download the Skin and Bones app before your visit', 'Bring headphones', 'Allow 30–60 minutes in Bone Hall'],
    tags: ['Education', 'Museum', 'Location-Based'],
    contentType: 'education',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['phone-ar'],
    comfortRating: 'comfortable',
    ageRating: 'all',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },

  // ========== LOCATION-BASED ==========
  // Physical immersive experiences — step into designed spaces that use tech and other interactive elements to surround you with story, visuals, and play.
  {
    id: 'teamlab-superblue',
    title: 'teamLab: Every Wall is a Door',
    slug: 'teamlab-every-wall-is-a-door',
    creatorName: 'teamLab',
    creatorId: 'creator-teamlab',
    creatorVerified: true,
    creatorExperienceCount: 12,
    primaryFormat: 'installation',
    formatBadge: 'Immersive Museum',
    formatDescriptor: 'Miami · Long-term',
    venueModel: 'specific-venue',
    category: 'location-based',
    categoryChip: 'Location-Based',
    deliveryTechnology: [],
    accessSurface: [],
    realityCheckPreview:
      'Wander through teamLab\'s responsive light rooms where your movement makes flowers bloom, scatter, and ripple through digital waterfalls — a living landscape that never repeats the same moment twice.',
    heroImageUrl: '/teamlab.png?v=3',
    thumbnail: '/teamlab.png?v=3',
    galleryUrls: ['/teamlab.png?v=3'],
    officialDescription:
      'Step inside Superblue Miami\'s 50,000-square-foot experiential art center in Allapattah, where teamLab\'s suite of installations fills darkened rooms with floor-to-ceiling projections that never hold still.\n\n' +
      'In Between Life and Non-Life, you walk through fields of digital flowers that bloom at your feet and scatter when others pass nearby — each petal rendered in real time by algorithms, so the scene you see has never existed before and will not reappear. Universe of Water Particles wraps you in a luminous waterfall that bends and flows around your body; Proliferating Immense Life responds to touch and seasonal cycles, evolving hour by hour through spring, summer, autumn, and winter.\n\n' +
      'Massless Clouds Between Sculpture and Life invites hands-on interaction with cloud-like forms that shift density as you move through them. The one-way path through the venue keeps the atmosphere contemplative even on busy days — low light, ambient sound, and soft transitions between rooms that feel more like stepping between worlds than browsing a gallery.\n\n' +
      'Works rotate within Superblue\'s long-term program alongside installations by Es Devlin, James Turrell, and Rafael Lozano-Hemmer\'s Pulse Topology, but the teamLab rooms remain the heart of the visit: responsive, collective, and built around the idea that your presence — and everyone else\'s — continuously rewrites the artwork.',
    description:
      'teamLab: Between Life and Non-Life at Superblue Miami is a suite of interconnected installations where audience movement shapes the art in real time.',
    url: 'https://www.superblue.com/miami',
    platforms: [],
    devices: [],
    priceType: 'ticket',
    priceLabel: 'Tickets from $32 weekdays · $39 weekends · Student & senior discounts available',
    publishPlatform: 'Superblue Miami',
    accessDevices: ['mobile', 'headset', 'desktop'],
    comfortRating: 'comfortable',
    ageRating: 'all-ages',
    recommendedVisitTime: 'Plan 90–120 minutes',
    typicalSessionLabel: '90–120 minutes',
    availabilityType: 'permanent',
    availabilityStatus: 'open-today',
    availabilityStatusLabel: 'Open daily · timed tickets required',
    venueHours: 'Mon–Thu 11am–7pm · Fri–Sat 10am–8pm · Sun 10am–7pm',
    venueName: 'Superblue Miami',
    venueAddress: '1101 NW 23rd Street',
    venueCity: 'Miami, FL 33127',
    venueCountry: 'US',
    locationCountry: 'United States',
    locationCity: 'Miami',
    venueLat: 25.7985,
    venueLng: -80.2139,
    venueParking: 'Metered street parking on NW 23rd St; valet available',
    venueTransit: 'Brightline Miami Central Station with direct service to the venue',
    venueAccessibilityEntrance: 'ADA-compliant venue; main entrance on NW 23rd Street between 11th and 12th Avenues',
    comePrepared: ['Wear comfortable shoes', 'Arrive at your timed entry', 'Book tickets online in advance'],
    bestFor: ['Families', 'Date Night', 'Art Lovers'],
    effortSignal: 'Timed tickets — book online in advance',
    // Full Details — confirmed: sessionStructure (timed entry), languages (English at US venue)
    // Inferred: themes, activities, replayability, guidanceLevel, groupSize
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['art', 'nature'],
    activities: ['explore', 'watch'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Small groups'],
    verifiedBy: 'creator',
    lastUpdated: 'June 2026',
    editorialBadge: 'Staff Pick',
    contentType: 'lbe',
    medium: 'lbe',
    isLBE: true,
    launchInBrowser: false,
    supportedDevices: ['lbe'],
    duration: 'Superblue Miami, Florida',
    tags: ['LBE', 'Installation', 'Art']
  },
  {
    id: 'meow-wolf-omega-mart',
    title: 'Meow Wolf – Omega Mart',
    slug: 'meow-wolf-omega-mart-las-vegas',
    creatorName: 'Meow Wolf',
    creatorId: 'creator-meow-wolf',
    creatorVerified: true,
    primaryFormat: 'installation',
    formatBadge: 'Immersive Art',
    formatDescriptor: 'Las Vegas · Permanent',
    venueModel: 'specific-venue',
    category: 'location-based',
    categoryChip: 'Location-Based',
    realityCheckPreview:
      'A surreal supermarket in Las Vegas that hides entire worlds behind ordinary aisles — walk-through art, puzzles, and a branching story you uncover by exploring.',
    description: 'Interactive walk-through experience. Surreal supermarket environment that unfolds into hidden rooms, puzzles, and a branching narrative as visitors explore.',
    officialDescription:
      'Omega Mart is Meow Wolf\'s immersive installation in Las Vegas — a fictional supermarket that serves as the entrance to a much stranger world. Products on shelves are jokes and clues; hidden doors lead to neon forests, desert portals, and narrative threads about the fictional Dramcorp corporation.\n\n' +
      'Most visitors spend 60–90 minutes exploring at their own pace. You can follow the main narrative or simply wander — replayability comes from spotting details you missed the first time. All ages welcome, though some areas are dark or disorienting.\n\n' +
      'Tickets are timed; book online in advance for weekends and holidays. Student and senior discounts available. Located in Area15 on the Las Vegas Strip.',
    thumbnail: '/meowwolf.png?v=3',
    heroImageUrl: '/meowwolf.png?v=3',
    galleryUrls: ['/meowwolf.png?v=3'],
    url: 'https://meowwolf.com/visit/las-vegas',
    duration: 'Las Vegas, Nevada',
    recommendedVisitTime: 'Plan 60–90 minutes',
    typicalSessionLabel: '60–90 minutes',
    priceType: 'ticket',
    priceLabel: 'Tickets from $49 · Student & senior discounts available',
    publishPlatform: 'Omega Mart at Area15',
    accessDevices: ['mobile', 'headset', 'desktop'],
    deliveryTechnology: [],
    accessSurface: [],
    availabilityType: 'permanent',
    availabilityStatus: 'open-today',
    availabilityStatusLabel: 'Open daily · timed tickets required',
    venueHours: 'Hours vary — check website for current schedule',
    venueName: 'Omega Mart at Area15',
    venueAddress: '3215 S Rancho Dr',
    venueCity: 'Las Vegas, NV 89102',
    venueCountry: 'US',
    locationCountry: 'United States',
    locationCity: 'Las Vegas',
    venueLat: 36.1311,
    venueLng: -115.1813,
    venueParking: 'Free parking at Area15',
    venueTransit: 'Short rideshare from Strip hotels',
    venueAccessibilityEntrance: 'ADA-accessible routes available — contact venue for details',
    comePrepared: ['Book timed tickets online', 'Wear comfortable shoes', 'Allow 60–90 minutes'],
    bestFor: ['Families', 'Art Lovers', 'Date Night'],
    effortSignal: 'Timed tickets — book online in advance',
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['art', 'narrative'],
    activities: ['explore'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Small groups', 'Families'],
    tags: ['LBE', 'Installation', 'Art'],
    contentType: 'lbe',
    medium: 'lbe',
    isLBE: true,
    launchInBrowser: false,
    supportedDevices: ['lbe'],
    comfortRating: 'comfortable',
    ageRating: 'all-ages',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },
  {
    id: 'netflix-house-dallas',
    title: 'Netflix House Dallas',
    slug: 'netflix-house-dallas',
    creatorName: 'Netflix',
    creatorId: 'creator-netflix',
    creatorVerified: true,
    primaryFormat: 'installation',
    formatBadge: 'Immersive Entertainment',
    formatDescriptor: 'Dallas · Permanent',
    venueModel: 'specific-venue',
    category: 'location-based',
    categoryChip: 'Location-Based',
    realityCheckPreview:
      'Step inside Netflix stories IRL — Stranger Things missions, Squid Game trials, and arcade-style experiences based on the shows you binge.',
    description: 'Experience Netflix stories IRL – from a Stranger Things rescue mission and Squid Game trials, to Netflix-themed arcade games and more.',
    officialDescription:
      'Netflix House Dallas is a permanent immersive venue where Netflix series become walk-through, interactive experiences. Zones inspired by Stranger Things, Squid Game, and other hits mix set-piece photography, mini-games, and retail.\n\n' +
      'Admission is free to explore the venue. Paid add-ons: feature experiences from $39, VR games from $25, mini-golf from $15, arcade cards from $10. Plan 60–120 minutes depending on how many zones you complete.\n\n' +
      'Located at Galleria Dallas. Book online for popular times; age ratings vary by zone (Squid Game experiences may require 12+).',
    thumbnail: '/netflix-house.png?v=3',
    heroImageUrl: '/netflix-house.png?v=3',
    galleryUrls: ['/netflix-house.png?v=3'],
    url: 'https://www.netflix.com/house/locations/dallas',
    duration: 'Dallas, Texas',
    recommendedVisitTime: 'Plan 60–120 minutes',
    typicalSessionLabel: '60–120 minutes',
    priceType: 'ticket',
    priceLabel: 'Free entry · Feature experiences from $39 · Mini-golf from $15 · Arcade from $10',
    publishPlatform: 'Netflix House Dallas',
    accessDevices: ['mobile', 'headset', 'desktop'],
    deliveryTechnology: [],
    accessSurface: [],
    availabilityType: 'permanent',
    availabilityStatus: 'open-today',
    availabilityStatusLabel: 'Open at Galleria Dallas · book online',
    venueHours: 'Mall hours — check Netflix House site for experience times',
    venueName: 'Netflix House',
    venueAddress: '13350 Dallas Pkwy',
    venueCity: 'Dallas, TX 75240',
    venueCountry: 'US',
    locationCountry: 'United States',
    locationCity: 'Dallas',
    venueLat: 32.9296,
    venueLng: -96.8197,
    venueParking: 'Galleria Dallas parking garages',
    venueTransit: 'Near Dallas Tollway · rideshare drop-off at Galleria',
    comePrepared: ['Book tickets online for popular zones', 'Check age ratings per experience', 'Wear comfortable shoes'],
    bestFor: ['Netflix Fans', 'Families', 'Groups'],
    effortSignal: 'Some zones require separate tickets',
    sessionStructure: 'single-session',
    languages: ['English'],
    themes: ['narrative', 'games'],
    activities: ['explore', 'play'],
    replayability: 'medium',
    guidanceLevel: 'self-guided',
    groupSize: ['Pairs', 'Small groups', 'Families'],
    tags: ['LBE', 'Interactive', 'TV'],
    contentType: 'lbe',
    medium: 'lbe',
    isLBE: true,
    launchInBrowser: false,
    supportedDevices: ['lbe'],
    comfortRating: 'comfortable',
    ageRating: '12+',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },
  {
    id: 'sphere-las-vegas',
    title: 'Sphere',
    slug: 'sphere-las-vegas',
    creatorName: 'Sphere Entertainment',
    creatorId: 'creator-sphere',
    creatorVerified: true,
    primaryFormat: 'immersive-theater',
    formatBadge: 'Immersive Venue',
    formatDescriptor: 'Las Vegas · Live shows',
    venueModel: 'specific-venue',
    category: 'location-based',
    categoryChip: 'Location-Based',
    realityCheckPreview:
      'The world\'s largest spherical venue — immersive concerts and spectacles at unprecedented scale, with wraparound visuals and sensory technology built for live events.',
    description: 'A next-generation entertainment venue redefining live shows — immersive concerts and experiences that transport audiences to places real and imagined, powered by sensory technology at an unprecedented scale.',
    officialDescription:
      'Sphere in Las Vegas is a next-generation entertainment venue featuring the largest LED screen on Earth, immersive audio, and haptic seating for select shows. Resident and touring acts — including U2: UV Achtung Baby Live at Sphere and Darren Aronofsky\'s Postcard from Earth — redefine what a live concert or spectacle can feel like.\n\n' +
      'Ticket prices vary widely by artist and seat location — from accessible gallery seats to premium floor packages. Most shows run 90–120 minutes. The venue is fully seated; no VR headset required — the building itself is the immersive technology.\n\n' +
      'Book early for popular dates. Located east of the Strip on Sands Avenue with dedicated parking and rideshare zones.',
    thumbnail: '/sphere.png',
    heroImageUrl: '/sphere.png',
    galleryUrls: ['/sphere.png'],
    url: 'https://www.thesphere.com/',
    duration: 'Las Vegas, Nevada',
    recommendedVisitTime: 'Plan 90–120 minutes per show',
    typicalSessionLabel: '90–120 minutes',
    priceType: 'ticket',
    priceLabel: 'Immersive films $95–$250 · Concerts from $175 · Student discounts on select dates',
    publishPlatform: 'The Sphere box office',
    accessDevices: ['mobile', 'headset', 'desktop'],
    deliveryTechnology: [],
    accessSurface: [],
    availabilityType: 'scheduled',
    availabilityStatus: 'next-show',
    availabilityStatusLabel: 'Show dates change by week — check thesphere.com',
    venueHours: 'Event-based — see show calendar',
    venueName: 'Sphere',
    venueAddress: '255 Sands Ave',
    venueCity: 'Las Vegas, NV 89169',
    venueCountry: 'US',
    locationCountry: 'United States',
    locationCity: 'Las Vegas',
    venueLat: 36.1215,
    venueLng: -115.1657,
    venueParking: 'On-site parking — book with ticket or pay on arrival',
    venueTransit: 'Rideshare drop-off on Sands Ave',
    venueAccessibilityEntrance: 'ADA seating and assisted listening available',
    comePrepared: ['Book tickets early for popular shows', 'Arrive 30 minutes before showtime', 'Check bag policy online'],
    bestFor: ['Concert Fans', 'Spectacle Seekers', 'Date Night'],
    effortSignal: 'Show tickets sell out — book early',
    sessionStructure: 'live-event',
    languages: ['English'],
    themes: ['music', 'art'],
    activities: ['watch'],
    replayability: 'medium',
    guidanceLevel: 'hosted',
    groupSize: ['Solo', 'Pairs', 'Small groups'],
    tags: ['LBE', 'Concerts', 'Live Events'],
    contentType: 'live-events',
    medium: 'lbe',
    isLBE: true,
    launchInBrowser: false,
    supportedDevices: ['lbe'],
    comfortRating: 'comfortable',
    ageRating: 'all-ages',
    verifiedBy: 'creator',
    lastUpdated: 'June 2026',
    editorialBadge: 'Staff Pick'
  },

  // ========== METAVERSE ==========
  // Live worlds and social spaces — attend events, explore shared worlds, and hang out with others in real time.
  {
    id: 'vrchat-discover-worlds',
    title: 'VR Chat: Discover Worlds',
    category: 'metaverse',
    categoryChip: 'Metaverse',
    primaryFormat: 'social-vr',
    formatBadge: 'Social VR Platform',
    formatDescriptor: 'Free · Download required',
    venueModel: 'virtual-world',
    realityCheckPreview:
      'The gateway to user-built VR worlds — download VRChat, then explore concerts, games, art spaces, and hangouts created by a global community.',
    description: 'Explore epic, imaginative worlds built by creators and meet people worldwide.',
    officialDescription:
      'VRChat is a free social VR platform where users create and share worlds — from game show stages and art galleries to nightclubs and cozy hangouts. Download on Meta Quest or Steam for full VR, or use the iPhone/iPad mobile app for a non-VR social experience.\n\n' +
      'There is no native Apple Vision Pro app — VRChat on Apple devices is the iOS/iPadOS App Store app (mobile), not visionOS. PC desktop client is also available without a headset.\n\n' +
      'This listing is the platform itself — individual worlds and events inside VRChat are listed separately with Join World CTAs. VRChat accounts are free; optional VRChat Plus subscription adds perks but is not required.',
    thumbnail: '/vrchat.png?v=3',
    heroImageUrl: '/vrchat.png?v=3',
    galleryUrls: ['/vrchat.png?v=3'],
    url: 'https://hello.vrchat.com/',
    duration: 'Social VR',
    typicalSessionLabel: 'Open-ended sessions',
    sessionStructure: 'persistent',
    isPlatformApp: true,
    deliveryTechnology: ['native-app'],
    accessSurface: ['headset-app', 'desktop-app'],
    availabilityType: 'persistent',
    availabilityStatus: 'available-now',
    availabilityStatusLabel: 'Free platform · download to enter',
    priceType: 'free',
    priceLabel: 'Free · VRChat Plus $9.99/month or $99.99/year (optional)',
    publishPlatform: 'Meta Quest & Steam (PC VR)',
    alsoAvailableOn: ['iPhone & iPad App Store (mobile)', 'Windows desktop app'],
    accessDevices: ['mobile', 'headset', 'desktop'],
    platforms: ['quest', 'pcvr', 'ios'],
    devices: ['quest', 'pcvr', 'ios'],
    languages: ['English'],
    themes: ['social'],
    activities: ['explore', 'play'],
    replayability: 'high',
    guidanceLevel: 'self-guided',
    groupSize: ['Solo', 'Pairs', 'Small groups'],
    bestFor: ['Social VR', 'Creators', 'Nightlife'],
    effortSignal: 'Download app first — then pick a world',
    comePrepared: ['Create a VRChat account', 'Set up mic and avatar', 'Use safety tools in public worlds'],
    tags: ['Metaverse', 'Social', 'Explore'],
    contentType: 'games',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr', 'phone'],
    comfortRating: 'moderate',
    ageRating: '13+',
    multiplayer: true,
    verifiedBy: 'editorial',
    lastUpdated: 'June 2026'
  },
  {
    id: 'punchline-comedy-vrchat',
    title: 'Punchline Comedy Club in VRChat',
    category: 'metaverse',
    categoryChip: 'Metaverse',
    primaryFormat: 'social-vr',
    formatBadge: 'Live Comedy World',
    formatDescriptor: 'VRChat · Scheduled',
    venueModel: 'virtual-world',
    hostPlatform: 'vrchat',
    realityCheckPreview:
      'A live comedy open mic inside VRChat — perform or watch with a real audience from around the world every February.',
    description: 'Comedy Open Mic. Perform or watch with a live audience from around the world.',
    officialDescription:
      'Punchline Comedy Club is a recurring live comedy open mic hosted inside VRChat. Performers sign up to take the virtual stage; audience members laugh, react, and socialize in real time — no travel required.\n\n' +
      'Requires VRChat installed (free). Runs on select schedules — typically February evenings PST. Join via the world link on vrcomedy.net or search inside VRChat. You can join from a VR headset or the VRChat desktop app.\n\n' +
      'Free to attend. 18+ for open mic content. Microphone required if you plan to perform.',
    thumbnail: '/punchline.png',
    heroImageUrl: '/punchline.png',
    galleryUrls: ['/punchline.png'],
    url: 'https://vrcomedy.net/',
    duration: 'Every February 6pm – 7:30pm PST',
    typicalSessionLabel: '90 minutes',
    sessionStructure: 'live-event',
    hostPlatform: 'vrchat',
    availabilityType: 'scheduled',
    availabilityStatus: 'next-show',
    availabilityStatusLabel: 'February open mic · 6–7:30pm PST',
    priceType: 'free',
    priceLabel: 'Free · VRChat account required',
    publishPlatform: 'Inside VRChat',
    alsoAvailableOn: ['Meta Quest', 'Steam (PC VR)', 'Windows desktop app'],
    accessDevices: ['headset', 'desktop'],
    deliveryTechnology: [],
    accessSurface: ['headset-app', 'desktop-app'],
    languages: ['English'],
    themes: ['social', 'comedy'],
    activities: ['watch', 'play'],
    replayability: 'medium',
    guidanceLevel: 'hosted',
    groupSize: ['Solo', 'Pairs', 'Small groups'],
    bestFor: ['Comedy Fans', 'Performers', 'Social VR'],
    effortSignal: 'VRChat app required · check schedule',
    comePrepared: ['Install VRChat first', 'Create account and avatar', 'Check vrcomedy.net for next date'],
    tags: ['Metaverse', 'Comedy', 'Live'],
    contentType: 'live-events',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr'],
    comfortRating: 'comfortable',
    ageRating: '18+',
    multiplayer: true,
    verifiedBy: 'creator',
    lastUpdated: 'June 2026'
  },
  {
    id: 'uncanny-alley',
    title: 'Uncanny Alley',
    category: 'metaverse',
    categoryChip: 'Metaverse',
    slug: 'uncanny-alley-vrchat',
    creatorName: 'Ferryman Collective',
    creatorId: 'creator-ferryman',
    creatorVerified: true,
    primaryFormat: 'social-vr',
    formatBadge: 'Live Interactive Theater',
    formatDescriptor: 'VRChat · Select showtimes',
    venueModel: 'virtual-world',
    hostPlatform: 'vrchat',
    realityCheckPreview:
      'Award-winning live interactive theater in VRChat — a dystopian escape narrative where your choices matter, from Ferryman Collective.',
    description: 'Award-winning live interactive theater by Ferryman Collective. Dystopian escape to find a new home. Select showtimes. VRChat on MetaQuest, SteamVR.',
    officialDescription:
      'Uncanny Alley is an award-winning live interactive theater experience by Ferryman Collective, performed inside VRChat. Audience members become participants in a dystopian escape narrative — exploring environments, interacting with performers, and shaping outcomes across select showtimes.\n\n' +
      'Requires VRChat on Meta Quest, SteamVR, or the Windows desktop app. Tickets and showtimes are listed on ferrymancollective.com — book ahead as capacity is limited. Typical run time is 60–90 minutes with live actors guiding the experience.\n\n' +
      'Not a solo app — this is a scheduled world event inside VRChat. Arrive early, use headphones, and follow host instructions for the best experience.',
    thumbnail: '/uncanny-alley.png?v=3',
    heroImageUrl: '/uncanny-alley.png?v=3',
    galleryUrls: ['/uncanny-alley.png?v=3'],
    url: 'https://www.ferrymancollective.com/uncanny-alley',
    duration: 'Select Showtimes',
    typicalSessionLabel: '60–90 minutes',
    sessionStructure: 'live-event',
    hostPlatform: 'vrchat',
    availabilityType: 'scheduled',
    availabilityStatus: 'limited-availability',
    availabilityStatusLabel: 'Select showtimes · book on Ferryman site',
    priceType: 'ticket',
    priceLabel: 'Tickets from $25 · book at Ferryman Collective',
    publishPlatform: 'Inside VRChat',
    alsoAvailableOn: ['Meta Quest', 'Steam (PC VR)', 'Windows desktop app'],
    accessDevices: ['headset', 'desktop'],
    deliveryTechnology: [],
    accessSurface: ['headset-app', 'desktop-app'],
    languages: ['English'],
    themes: ['narrative', 'sci-fi'],
    activities: ['explore', 'watch'],
    replayability: 'medium',
    guidanceLevel: 'guided',
    groupSize: ['Small groups'],
    bestFor: ['Theater Fans', 'Interactive Story', 'Social VR'],
    effortSignal: 'Book showtime · VRChat required',
    comePrepared: ['Install VRChat before showtime', 'Book ticket on Ferryman site', 'Use headphones and arrive early'],
    tags: ['Metaverse', 'Theater', 'Interactive'],
    contentType: 'live-events',
    medium: 'metaverse',
    isLBE: false,
    launchInBrowser: false,
    supportedDevices: ['quest', 'pcvr'],
    comfortRating: 'moderate',
    ageRating: '12+',
    multiplayer: true,
    verifiedBy: 'creator',
    lastUpdated: 'June 2026',
    editorialBadge: 'Staff Pick'
  }
];

export function getExperienceById(id) {
  return XR_EXPERIENCES.find(exp => exp.id === id) || null;
}

export const CATEGORIES = [
  { id: 'for-you', label: 'For You', color: '#FF6B6B' },
  { id: '360-video', label: '360°', color: '#4ECDC4' },
  { id: 'augmented-reality', label: 'Augmented Reality', color: '#45B7D1' },
  { id: 'location-based', label: 'Location-Based', color: '#e85d04' },
  { id: 'metaverse', label: 'Metaverse', color: '#9d4edd' },
  { id: 'virtual-reality', label: 'Virtual Reality', color: '#2196F3' }
];

// Category descriptions for section headers
export const CATEGORY_DESCRIPTIONS = {
  'for-you': 'Recommended for you',
  '360-video': 'Look around in every direction from a fixed viewpoint — using your phone, browser, or headset.',
  'augmented-reality': 'Place digital content in your space — explore, tap, move, and play.',
  'location-based': 'Physical immersive experiences — step into designed spaces that use tech and other interactive elements to surround you with story, visuals, and play.',
  'metaverse': 'Live worlds and social spaces — attend events, explore shared worlds, and hang out with others in real time.',
  'virtual-reality': 'Headset-based experiences — play, explore, and participate inside fully immersive digital environments.'
};
